const TIMELINE_PANEL = '.timeline-right-panel';

export type TimelineScrollbarElements = {
    thumb: HTMLElement;
    track: HTMLElement;
    content: HTMLElement;
};

export function getTimelineScrollbarElements(): TimelineScrollbarElements | null {
    const thumb = document.getElementById('scrollbarThumb');
    const track = document.querySelector(`${TIMELINE_PANEL} .scrollbar-track`) as HTMLElement | null;
    const content = document.querySelector(`${TIMELINE_PANEL} .timeline-content`) as HTMLElement | null;
    if (!thumb || !track || !content) return null;
    return { thumb, track, content };
}

export function updateTimelineScrollbarFull(el: TimelineScrollbarElements): void {
    const { thumb: scrollbarThumb, track: scrollbarTrack, content: timelineContent } = el;

    const trackWidth = scrollbarTrack.offsetWidth;
    const scrollWidth = timelineContent.scrollWidth;
    const clientWidth = timelineContent.clientWidth;

    if (trackWidth <= 0 || scrollWidth <= 0 || clientWidth <= 0) return;

    let visiblePercentage = clientWidth / scrollWidth;
    visiblePercentage = Math.max(0, Math.min(1, visiblePercentage));
    const maxVisiblePercentage = 0.15;
    if (visiblePercentage > maxVisiblePercentage) {
        visiblePercentage = maxVisiblePercentage;
    }

    const minThumbPx = 20;
    let thumbWidth = trackWidth * visiblePercentage;
    if (thumbWidth < minThumbPx) {
        thumbWidth = minThumbPx;
    }

    scrollbarThumb.style.width = `${thumbWidth}px`;

    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const maxThumbTravel = Math.max(0, trackWidth - thumbWidth);
    const percentage = maxScroll === 0 ? 0 : timelineContent.scrollLeft / maxScroll;
    scrollbarThumb.style.left = `${percentage * maxThumbTravel}px`;
}

export function updateTimelineScrollbarPositionOnly(el: TimelineScrollbarElements): void {
    const { thumb: scrollbarThumb, track: scrollbarTrack, content: timelineContent } = el;

    const trackWidth = scrollbarTrack.offsetWidth;
    const scrollWidth = timelineContent.scrollWidth;
    const clientWidth = timelineContent.clientWidth;
    const thumbWidth = scrollbarThumb.offsetWidth;

    if (trackWidth <= 0 || scrollWidth <= 0 || clientWidth <= 0 || thumbWidth <= 0) return;

    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const maxThumbTravel = Math.max(0, trackWidth - thumbWidth);
    const percentage = maxScroll === 0 ? 0 : timelineContent.scrollLeft / maxScroll;
    scrollbarThumb.style.left = `${percentage * maxThumbTravel}px`;
}

/**
 * 绑定底部自定义滚动条与 .timeline-content 的双向同步，返回一次性 destroy（移除全部监听器）。
 */
export function attachTimelineScrollbarInteractions(
    el: TimelineScrollbarElements,
    onScrollSync: () => void,
): () => void {
    const { thumb: scrollbarThumb, track: scrollbarTrack, content: timelineContent } = el;

    let isDragging = false;
    let thumbOffsetX = 0;
    let trackWidth = 0;
    let thumbWidth = 0;
    let maxScroll = 0;
    let trackLeft = 0;

    const updateScrollPosition = (clientX: number) => {
        if (!trackWidth || !thumbWidth || !maxScroll) return;

        const mouseX = clientX - trackLeft;
        const maxThumbTravel = trackWidth - thumbWidth;
        if (maxThumbTravel <= 0) return;

        let targetThumbLeft = mouseX - thumbOffsetX;
        targetThumbLeft = Math.max(0, Math.min(maxThumbTravel, targetThumbLeft));

        const percentage = maxThumbTravel === 0 ? 0 : targetThumbLeft / maxThumbTravel;
        const newScrollLeft = percentage * maxScroll;

        scrollbarThumb.style.left = `${targetThumbLeft}px`;
        timelineContent.scrollLeft = newScrollLeft;
    };

    const onThumbMouseDown = (e: MouseEvent) => {
        isDragging = true;
        e.preventDefault();
        e.stopPropagation();

        const trackRect = scrollbarTrack.getBoundingClientRect();
        const thumbRect = scrollbarThumb.getBoundingClientRect();

        thumbOffsetX = e.clientX - thumbRect.left;
        trackLeft = trackRect.left;
        trackWidth = trackRect.width;
        thumbWidth = scrollbarThumb.offsetWidth;
        maxScroll = Math.max(0, timelineContent.scrollWidth - timelineContent.clientWidth);

        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
        timelineContent.style.scrollBehavior = 'auto';
    };

    const onDocumentMouseMove = (e: MouseEvent) => {
        if (isDragging) {
            e.preventDefault();
            updateScrollPosition(e.clientX);
        }
    };

    const onDocumentMouseUp = (e: MouseEvent) => {
        if (isDragging) {
            isDragging = false;
            e.preventDefault();

            document.body.style.userSelect = '';
            document.body.style.cursor = '';
            timelineContent.style.scrollBehavior = '';
        }
    };

    const onTrackMouseDown = (e: MouseEvent) => {
        if (e.target === scrollbarThumb) return;

        const mouseEvent = e as MouseEvent;
        const trackRect = scrollbarTrack.getBoundingClientRect();
        const clickX = mouseEvent.clientX - trackRect.left;
        const trackWidthLocal = trackRect.width;
        const thumbWidthLocal = scrollbarThumb.offsetWidth;
        const maxScrollLocal = Math.max(0, timelineContent.scrollWidth - timelineContent.clientWidth);

        if (trackWidthLocal <= 0 || maxScrollLocal <= 0) return;

        const maxThumbTravel = trackWidthLocal - thumbWidthLocal;
        const targetThumbLeft = Math.max(0, Math.min(maxThumbTravel, clickX - thumbWidthLocal / 2));
        const percentage = maxThumbTravel === 0 ? 0 : targetThumbLeft / maxThumbTravel;
        const targetScrollLeft = percentage * maxScrollLocal;

        scrollbarThumb.style.left = `${targetThumbLeft}px`;
        timelineContent.scrollLeft = targetScrollLeft;
    };

    const onTimelineScroll = () => {
        requestAnimationFrame(() => {
            onScrollSync();
        });
    };

    scrollbarThumb.addEventListener('mousedown', onThumbMouseDown);
    document.addEventListener('mousemove', onDocumentMouseMove, { passive: false });
    document.addEventListener('mouseup', onDocumentMouseUp);
    scrollbarTrack.addEventListener('mousedown', onTrackMouseDown);
    timelineContent.addEventListener('scroll', onTimelineScroll, { passive: true });

    return () => {
        scrollbarThumb.removeEventListener('mousedown', onThumbMouseDown);
        document.removeEventListener('mousemove', onDocumentMouseMove);
        document.removeEventListener('mouseup', onDocumentMouseUp);
        scrollbarTrack.removeEventListener('mousedown', onTrackMouseDown);
        timelineContent.removeEventListener('scroll', onTimelineScroll);
    };
}
